/**
 * Proof of Unique Human - the part that decides whether a face is new.
 *
 * A raw face embedding must never reach the network. What a claimant
 * publishes instead is a *cancelable biometric code*: the signs of 384
 * fixed random projections of the L2-normalised 128-D embedding, under a
 * public network pepper. Properties that matter:
 *
 *   - One-way. Sign bits discard all magnitude information; no face image
 *     can be reconstructed from the code.
 *   - Locality sensitive. For unit vectors, Pr[sign differs on a random
 *     hyperplane] = theta / pi. Two captures of the same face (theta about
 *     0.45 rad) differ on roughly 14% of bits; two different faces (theta
 *     about 1.45 rad) differ on roughly 46%. The gap is what makes
 *     "is this face already on the ledger?" answerable by Hamming distance
 *     alone, with no embedding on the wire.
 *   - Bandable. The code splits into 32 bands of 12 bits. Two codes that
 *     are close collide on at least one whole band with high probability,
 *     so duplicate lookup is an O(1) hash-index probe rather than a scan
 *     of every identity ever registered.
 *   - Cancelable. Change the pepper and every code changes, so a leaked
 *     index cannot be replayed against a future version of the network.
 *
 * Honest limits are documented in WHITEPAPER.md section 4: a published
 * code is a stable pseudonymous biometric identifier. It cannot rebuild a
 * face, but two ledgers using the same pepper could be cross-linked. That
 * is the price of enforcing one-human-one-mint without a central issuer,
 * and it is why the ledger is encrypted at rest and why PRIVATE_MODE
 * (band hashes only, no full code) exists for jurisdictions where even a
 * pseudonymous code is dangerous.
 */

import { sha256 } from '@noble/hashes/sha2';
import { concat, fromHex, toHex, utf8, type Hex } from './bytes.ts';
import { tag } from './hash.ts';
import { params } from './params.ts';
import { SKETCH_BITS } from './fuzzy.ts';

export const EMBEDDING_DIMS = 128;
export const CODE_BITS = 384;
/**
 * Bits used for the wallet-recovery sketch. Taken from the fuzzy extractor
 * rather than restated, because a mismatch between the two would only show
 * up as recovery failing for real people.
 */
export const SKETCH_CODE_BITS = SKETCH_BITS;
export const CODE_BYTES = CODE_BITS / 8; // 48
export const BAND_BITS = 12;
export const BANDS = CODE_BITS / BAND_BITS; // 32
export const BAND_HASH_BYTES = 16;

/** At least this many whole bands must collide before two codes are compared. */
export const BAND_MATCH_MIN = 2;
/**
 * Fraction of differing bits below which two codes are ruled the same human.
 * Sits in the empty space between the same-face mode (~0.14) and the
 * different-face mode (~0.46).
 */
export const HAMMING_DUPLICATE_MAX = 0.28;
/** Above this, definitely different people. Between the two is "disputed". */
export const HAMMING_DISTINCT_MIN = 0.34;

/* ----------------------- projection matrix -------------------------- */

/**
 * The hyperplanes are not stored - they are regenerated from the pepper by
 * every node, so there is no matrix to distribute, tamper with or lose.
 * SHA-256 in counter mode is the XOF; Box-Muller turns it into gaussians.
 */
const matrixCache = new Map<string, Float64Array>();

/**
 * `rows` x `cols` standard gaussians, generated deterministically from a
 * domain tag and the pepper. Shared with the finger code in finger.ts so
 * there is one implementation of this to get wrong, not two.
 */
export function gaussianProjection(domain: string, pepper: string, rows: number, cols: number): Float64Array {
  const cacheKey = `${domain}#${pepper}#${rows}#${cols}`;
  const cached = matrixCache.get(cacheKey);
  if (cached) return cached;

  const need = rows * cols;
  const out = new Float64Array(need);
  const seed = tag(domain, utf8(pepper));

  let counter = 0;
  let pool: Uint8Array<ArrayBufferLike> = new Uint8Array(0);
  let poolOffset = 0;
  const nextU32 = (): number => {
    if (poolOffset + 4 > pool.length) {
      const ctr = new Uint8Array(4);
      new DataView(ctr.buffer).setUint32(0, counter++, false);
      pool = sha256(concat(seed, ctr));
      poolOffset = 0;
    }
    const v = (pool[poolOffset] << 24) | (pool[poolOffset + 1] << 16) | (pool[poolOffset + 2] << 8) | pool[poolOffset + 3];
    poolOffset += 4;
    return v >>> 0;
  };
  // Open interval (0,1): Box-Muller must never see exactly zero.
  const nextUnit = (): number => (nextU32() + 0.5) / 4294967296;

  for (let i = 0; i < need; i += 2) {
    const u = nextUnit();
    const v = nextUnit();
    const r = Math.sqrt(-2 * Math.log(u));
    out[i] = r * Math.cos(2 * Math.PI * v);
    if (i + 1 < need) out[i + 1] = r * Math.sin(2 * Math.PI * v);
  }

  matrixCache.set(cacheKey, out);
  return out;
}

/**
 * The hyperplanes are not stored - they are regenerated from the pepper by
 * every node, so there is no matrix to distribute, tamper with or lose.
 */
export function projectionMatrix(pepper = params().lshPepper, bits = CODE_BITS): Float64Array {
  return gaussianProjection('face/lsh/pepper/v1', pepper, bits, EMBEDDING_DIMS);
}

/* --------------------------- code + bands --------------------------- */

export function l2normalize(v: ArrayLike<number>): Float64Array {
  let s = 0;
  for (let i = 0; i < v.length; i++) s += v[i] * v[i];
  s = Math.sqrt(s) || 1;
  const out = new Float64Array(v.length);
  for (let i = 0; i < v.length; i++) out[i] = v[i] / s;
  return out;
}

function signBits(embedding: ArrayLike<number>, bits: number, pepper: string): Uint8Array {
  if (embedding.length !== EMBEDDING_DIMS) {
    throw new Error(`embedding must be ${EMBEDDING_DIMS}-D, got ${embedding.length}`);
  }
  const unit = l2normalize(embedding);
  const M = projectionMatrix(pepper, bits);
  const out = new Uint8Array(bits / 8);
  for (let p = 0; p < bits; p++) {
    let dot = 0;
    const base = p * EMBEDDING_DIMS;
    for (let i = 0; i < EMBEDDING_DIMS; i++) dot += unit[i] * M[base + i];
    if (dot >= 0) out[p >> 3] |= 1 << (p & 7);
  }
  return out;
}

/** 128-D embedding -> 48-byte cancelable code, used for uniqueness. */
export function encodeFaceCode(embedding: ArrayLike<number>, pepper = params().lshPepper): Uint8Array {
  return signBits(embedding, CODE_BITS, pepper);
}

/**
 * The wider bit string the wallet-recovery sketch is built on. A separate
 * pepper keeps it in a different projection space from the uniqueness
 * code, so the published helper cannot be repurposed as a second copy of
 * the identity index.
 */
export function faceSketchBits(embedding: ArrayLike<number>, pepper = params().lshPepper): Uint8Array {
  return signBits(embedding, SKETCH_CODE_BITS, pepper + '/sketch');
}

function bandBits(code: Uint8Array, band: number): Uint8Array {
  const out = new Uint8Array(2); // 12 bits fits in 2 bytes
  for (let j = 0; j < BAND_BITS; j++) {
    const bit = band * BAND_BITS + j;
    if ((code[bit >> 3] >> (bit & 7)) & 1) out[j >> 3] |= 1 << (j & 7);
  }
  return out;
}

/** The 32 band hashes published with a mint claim, used as the duplicate index. */
export function bandHashes(code: Uint8Array, pepper = params().lshPepper): Hex[] {
  if (code.length !== CODE_BYTES) throw new Error('bad code length');
  const out: Hex[] = [];
  for (let b = 0; b < BANDS; b++) {
    const idx = new Uint8Array([b]);
    out.push(toHex(tag('face/lsh/band/v1', utf8(pepper), idx, bandBits(code, b)).slice(0, BAND_HASH_BYTES)));
  }
  return out;
}

const POPCOUNT = new Uint8Array(256);
for (let i = 0; i < 256; i++) POPCOUNT[i] = (i & 1) + POPCOUNT[i >> 1];

export function hammingDistance(a: Uint8Array, b: Uint8Array): number {
  if (a.length !== b.length) throw new Error('length mismatch');
  let d = 0;
  for (let i = 0; i < a.length; i++) d += POPCOUNT[a[i] ^ b[i]];
  return d;
}

export function hammingFraction(a: Uint8Array, b: Uint8Array): number {
  return hammingDistance(a, b) / (a.length * 8);
}

/** Approximate angle between the two embeddings, recovered from the code. */
export function angleFromCodes(a: Uint8Array, b: Uint8Array): number {
  return hammingFraction(a, b) * Math.PI;
}

export type UniquenessVerdict = 'unique' | 'duplicate' | 'disputed';

export interface UniquenessResult {
  verdict: UniquenessVerdict;
  /** Identity id of the closest existing registration, if any. */
  nearestId?: string;
  nearestFraction?: number;
  bandCollisions: number;
}


export interface RegisteredIdentity {
  id: string;
  code: Hex;
  bands: Hex[];
  address: string;
  mintedAt: number;
  /** Secure-sketch helper and commitment, for wallet recovery from a scan. */
  sketch: Hex;
  commit: Hex;
  /**
   * The authenticator this wallet was bound to.
   *
   * Published so a device that has never seen this wallet can find the
   * credential from the ledger and ask the platform for it - which works
   * when passkeys have synced, and fails honestly when they have not.
   */
  device?: { id: string; pub: Hex };
}

/**
 * Two-stage duplicate detection: band index probe, then exact Hamming
 * comparison against only the candidates the probe surfaced.
 */
export class UniquenessIndex {
  private byBand = new Map<Hex, Set<string>>();
  private byId = new Map<string, RegisteredIdentity>();

  get size(): number {
    return this.byId.size;
  }

  has(id: string): boolean {
    return this.byId.has(id);
  }

  get(id: string): RegisteredIdentity | undefined {
    return this.byId.get(id);
  }

  all(): RegisteredIdentity[] {
    return [...this.byId.values()];
  }

  check(code: Uint8Array, bands: Hex[]): UniquenessResult {
    const counts = new Map<string, number>();
    for (const b of bands) {
      const ids = this.byBand.get(b);
      if (!ids) continue;
      for (const id of ids) counts.set(id, (counts.get(id) || 0) + 1);
    }

    let best: UniquenessResult = { verdict: 'unique', bandCollisions: 0 };
    let bestFrac = 1;
    for (const [id, collisions] of counts) {
      if (collisions < BAND_MATCH_MIN) continue;
      const other = this.byId.get(id);
      if (!other) continue;
      const frac = hammingFraction(code, fromHex(other.code));
      if (frac < bestFrac) {
        bestFrac = frac;
        best = {
          verdict: frac <= HAMMING_DUPLICATE_MAX ? 'duplicate' : frac < HAMMING_DISTINCT_MIN ? 'disputed' : 'unique',
          nearestId: id,
          nearestFraction: frac,
          bandCollisions: collisions,
        };
      }
    }
    return best;
  }

  register(identity: RegisteredIdentity): void {
    if (this.byId.has(identity.id)) return;
    this.byId.set(identity.id, identity);
    for (const b of identity.bands) {
      let set = this.byBand.get(b);
      if (!set) this.byBand.set(b, (set = new Set()));
      set.add(identity.id);
    }
  }

  remove(id: string): void {
    const it = this.byId.get(id);
    if (!it) return;
    this.byId.delete(id);
    for (const b of it.bands) {
      const set = this.byBand.get(b);
      if (!set) continue;
      set.delete(id);
      if (set.size === 0) this.byBand.delete(b);
    }
  }

  snapshot(): RegisteredIdentity[] {
    return this.all();
  }

  static restore(items: RegisteredIdentity[]): UniquenessIndex {
    const ix = new UniquenessIndex();
    for (const it of items) ix.register(it);
    return ix;
  }
}

/** Stable identity id: the code's own hash. Same human -> same-ish code, but
 *  the id is only a label; uniqueness is decided by Hamming distance. */
export function identityId(code: Uint8Array): string {
  return toHex(tag('face/identity/v1', code).slice(0, 16));
}
